import { useState } from "react";
import { Banknote, Copy, CreditCard, Link2, MessageCircle, X } from "lucide-react";

import { api, ApiError } from "@/lib/api-client";
import { aed, PAYMENT_KIND_LABEL, PAYMENT_METHOD_LABEL, type OrderFull, type PaymentKind, type PaymentMethod } from "@/lib/orders";
import { BTN_GHOST, BTN_GOLD, BTN_PRIMARY } from "./ui";

const STATUS: Record<string, string> = { pending: "link sent", paid: "paid", expired: "expired", cancelled: "cancelled" };

/**
 * Deposit and balance of a signed order. "Request" makes a Stripe Checkout
 * link (mailed to the customer, also on their order page); the webhook marks
 * it paid. Transfers, cash and POS are recorded by hand.
 */
export function PaymentsCard({ order, stripeConfigured, onChanged }: { order: OrderFull; stripeConfigured: boolean; onChanged: (o: OrderFull) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [manual, setManual] = useState<{ kind: PaymentKind; method: PaymentMethod; amount: string; note: string } | null>(null);
  const s = order.paymentSummary;
  const signed = order.status === "signed";

  const reload = async () => onChanged(await api.order(order.id));
  const run = async (name: string, fn: () => Promise<string | void>) => {
    setBusy(name);
    setNotice(null);
    try {
      const msg = await fn();
      await reload();
      if (msg) setNotice(msg);
    } catch (e) {
      let text = "Something went wrong.";
      if (e instanceof ApiError) {
        try {
          const m = (JSON.parse(e.message) as { message?: string | string[] }).message;
          text = Array.isArray(m) ? m.join(", ") : (m ?? text);
        } catch {
          text = e.message || text;
        }
      }
      setNotice(text);
    } finally {
      setBusy(null);
    }
  };

  const requestLink = (kind: PaymentKind) =>
    run(kind, async () => {
      const p = await api.requestPayment(order.id, { kind });
      if (p.checkoutUrl) void navigator.clipboard?.writeText(p.checkoutUrl).catch(() => undefined);
      return p.mailResult === "sent" ? `${PAYMENT_KIND_LABEL[kind]} link sent to ${order.email} and copied.` : `${PAYMENT_KIND_LABEL[kind]} link created and copied${order.email ? "" : " (no email on the order: share it by WhatsApp)"}.`;
    });

  const live = order.payments.filter((p) => p.status === "pending");
  const history = order.payments.filter((p) => p.status !== "pending");

  return (
    <section className="glass-panel mt-4 p-5">
      <div className="flex items-center justify-between">
        <div className="brand-kicker">Payments</div>
        <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ice/50">{stripeConfigured ? "Stripe on" : "Stripe not set up"}</span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-lg bg-night/60 p-2"><div className="text-ice/50">Paid</div><div className="num text-paper">{aed(s.paid)}</div></div>
        <div className="rounded-lg bg-night/60 p-2"><div className="text-ice/50">Pending</div><div className="num text-paper">{aed(s.pending)}</div></div>
        <div className="rounded-lg bg-night/60 p-2"><div className="text-ice/50">Outstanding</div><div className="num text-goldbright">{aed(s.outstanding)}</div></div>
      </div>

      {!signed && <p className="mt-3 text-xs text-ice/60">Payments open once the order is signed.</p>}

      {signed && s.outstanding > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {s.paid === 0 && (
            <button type="button" className={BTN_PRIMARY} disabled={busy !== null || !stripeConfigured} title={stripeConfigured ? "" : "Add the Stripe key in Settings → Orders"} onClick={() => void requestLink("deposit")}>
              <CreditCard className="h-3.5 w-3.5" /> {busy === "deposit" ? "Creating…" : `Request deposit ${aed(s.depositDue)}`}
            </button>
          )}
          <button type="button" className={BTN_GOLD} disabled={busy !== null || !stripeConfigured} title={stripeConfigured ? "" : "Add the Stripe key in Settings → Orders"} onClick={() => void requestLink("balance")}>
            <CreditCard className="h-3.5 w-3.5" /> {busy === "balance" ? "Creating…" : `Request ${s.paid === 0 ? "full amount" : "balance"} ${aed(s.balanceDue)}`}
          </button>
          <button type="button" className={BTN_GHOST} disabled={busy !== null} onClick={() => setManual({ kind: s.paid === 0 ? "deposit" : "balance", method: "bank_transfer", amount: String(s.paid === 0 ? s.depositDue : s.balanceDue), note: "" })}>
            <Banknote className="h-3.5 w-3.5" /> Record transfer / cash
          </button>
        </div>
      )}

      {manual && (
        <div className="mt-3 grid gap-2 rounded-xl border border-white/12 bg-night/50 p-3 text-sm sm:grid-cols-2">
          <label className="block"><span className="field-label">Instalment</span>
            <select className="field" value={manual.kind} onChange={(e) => setManual({ ...manual, kind: e.target.value as PaymentKind })}>
              {(["deposit", "balance", "custom"] as PaymentKind[]).map((k) => <option key={k} value={k} className="bg-night">{PAYMENT_KIND_LABEL[k]}</option>)}
            </select></label>
          <label className="block"><span className="field-label">Method</span>
            <select className="field" value={manual.method} onChange={(e) => setManual({ ...manual, method: e.target.value as PaymentMethod })}>
              {(["bank_transfer", "cash", "card_pos"] as PaymentMethod[]).map((m) => <option key={m} value={m} className="bg-night">{PAYMENT_METHOD_LABEL[m]}</option>)}
            </select></label>
          <label className="block"><span className="field-label">Amount AED</span>
            <input className="field num" type="number" min={0} step="0.01" dir="ltr" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} /></label>
          <label className="block"><span className="field-label">Reference / note</span>
            <input className="field" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} placeholder="transfer ref, receipt no." /></label>
          <div className="flex gap-2 sm:col-span-2">
            <button type="button" className={BTN_PRIMARY} disabled={busy !== null || !(Number(manual.amount) > 0)} onClick={() => void run("manual", async () => { await api.recordPayment(order.id, { kind: manual.kind, method: manual.method, amount: Number(manual.amount), note: manual.note || undefined }); setManual(null); return "Payment recorded."; })}>
              Save payment
            </button>
            <button type="button" className={BTN_GHOST} onClick={() => setManual(null)}>Cancel</button>
          </div>
        </div>
      )}

      {(live.length > 0 || history.length > 0) && (
        <ul className="mt-3 divide-y divide-white/8 text-sm">
          {[...live, ...history].map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div>
                <span className="text-paper">{PAYMENT_KIND_LABEL[p.kind]}</span>
                <span className="ml-2 text-xs text-ice/55">{PAYMENT_METHOD_LABEL[p.method]} · {STATUS[p.status] ?? p.status}{p.paidAt ? ` · ${new Date(p.paidAt).toLocaleDateString("en-GB")}` : ""}{p.note ? ` · ${p.note}` : ""}</span>
              </div>
              <div className="flex items-center gap-2">
                <b className={`num ${p.status === "paid" ? "text-green" : p.status === "pending" ? "text-goldbright" : "text-ice/50 line-through"}`}>{aed(p.amount)}</b>
                {p.status === "pending" && p.checkoutUrl && (
                  <>
                    <button type="button" className="text-ice/60 hover:text-paper" title="Copy payment link" onClick={() => { void navigator.clipboard.writeText(p.checkoutUrl!); setNotice("Payment link copied."); }}><Copy className="h-3.5 w-3.5" /></button>
                    <a className="text-ice/60 hover:text-paper" title="Open payment page" href={p.checkoutUrl} target="_blank" rel="noopener noreferrer"><Link2 className="h-3.5 w-3.5" /></a>
                    <button type="button" className="text-[#25D366] hover:brightness-125" title="Send the link on WhatsApp" disabled={busy !== null} onClick={() => void run("wa", async () => { const r = await api.whatsappPaymentLink(order.id, p.id); return r.result === "sent" ? "Sent on WhatsApp." : r.result === "off" ? "WhatsApp is not configured on this server." : `WhatsApp: ${r.result}`; })}><MessageCircle className="h-3.5 w-3.5" /></button>
                    <button type="button" className="text-ice/60 hover:text-pink" title="Cancel this link" disabled={busy !== null} onClick={() => void run("cancel", async () => { await api.cancelPaymentLink(order.id, p.id); return "Link cancelled."; })}><X className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {notice && <p className="mt-3 text-xs text-goldbright">{notice}</p>}
    </section>
  );
}
